import * as constants from "../constants/alert";
import shortid from "shortid/index";
import {
  SendAddAlert,
  AlertLevelType,
  SendRemoveAlert,
  AlertAction,
  AlertMessage,
  AlertOptions,
} from "../types/alert";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import { ApplicationState, ApplicationAction } from "../types";
import { I18NString } from "../types/modal";
import { FetchError } from "../types/error";
import { parseServerMessage, ServerError } from "./utils";
import { parseAlertMessage } from "../services/alert";

export function sendAddAlert(
  id: string,
  type: AlertLevelType,
  message: string | I18NString,
  options?: { icon?: string; dismissable?: boolean; html?: boolean }
): SendAddAlert {
  return {
    type: constants.SEND_ADD_ALERT,
    payload: {
      id,
      type,
      message,
      icon: options?.icon,
      dismissable: options?.dismissable,
      html: options?.html,
    },
  };
}

export function sendRemoveAlert(id: string): SendRemoveAlert {
  return {
    type: constants.SEND_REMOVE_ALERT,
    payload: id,
  };
}

export function addAlert(
  type: AlertLevelType,
  message: string | I18NString,
  options?: AlertOptions
): ThunkAction<void, ApplicationState, {}, AlertAction> {
  return (dispatch, getState) => {
    dispatch(sendAddAlert(shortid.generate(), type, message, options));
  };
}

function getMessageType(type: string) {
  if (type === "warning") {
    return constants.ALERT_LEVEL_WARNING;
  } else if (type === "danger") {
    return constants.ALERT_LEVEL_DANGER;
  }
  return constants.ALERT_LEVEL_DANGER;
}

/**Accepts only formatted message id as message */
export function dispatchError(
  messageId: string,
  e: any,
  dispatch: ThunkDispatch<ApplicationState, {}, ApplicationAction>,
  options?: any
): FetchError {
  return dispatchErrorV2(`formattedId::${messageId}`, e, dispatch, options);
}

export function dispatchErrorV2(
  message: string,
  e: any,
  dispatch: ThunkDispatch<ApplicationState, {}, ApplicationAction>,
  options?: any
): FetchError {
  let error: FetchError = { code: -1, message: "Unknown error" };
  if (e instanceof ServerError) {
    error = { code: e.code, message: e.message };
  } else if (typeof e.message == "string") {
    error = { code: -1, message: e.message };
  } else if (typeof e === "string") {
    error = { code: -1, message: e };
  }

  const { code, message: m } = error;

  let alertMessage: I18NString | null = null;
  let alertType: AlertLevelType = constants.ALERT_LEVEL_DANGER;
  /**Parse formatted id message */
  if (message.includes("formattedId::")) {
    const parsedData = parseAlertMessage(message, alertType);
    alertMessage = {
      id: parsedData.message.replace("formattedId::", ""),
      values: { error: `${parseServerMessage(code, m)}:${code}`, ...options },
    };
    alertType = parsedData.type;
  } else if (message.includes("<!doctype")) {
    /**Response text is error page */
    alertMessage = {
      id: "NPT_SERVER_MESSAGE_EMPTY_ERROR",
      values: { error: `${parseServerMessage(code, m)}:${code}`, ...options },
    };
  }
  /**Check if error message is plain text */
  dispatch(addAlert(alertType, alertMessage || message));
  return error;
}

/**Accepts only formatted message id as message */
export function dispatchSuccess(
  messageId: string,
  dispatch: ThunkDispatch<ApplicationState, {}, ApplicationAction>,
  options?: any
) {
  return dispatchSuccessV2(`formattedId::${messageId}`, dispatch, options);
}

export function dispatchSuccessV2(
  message: string,
  dispatch: ThunkDispatch<ApplicationState, {}, ApplicationAction>,
  options?: any
) {
  let alertMessage: I18NString | string = "";
  let alertType: AlertLevelType = constants.ALERT_LEVEL_SUCCESS;
  /**Parse formatted id message */
  if (message.includes("formattedId::")) {
    const parsedData = parseAlertMessage(message, alertType);
    alertMessage = {
      id: parsedData.message.replace("formattedId::", ""),
      values: { ...options },
    };
    alertType = parsedData.type;
  }
  dispatch(addAlert(alertType, alertMessage || message));
}

export function dispatchResponse(
  response: Response,
  dispatch: ThunkDispatch<ApplicationState, {}, ApplicationAction>,
  options?: { successMessage?: AlertMessage; failMessage?: AlertMessage }
): { valid: boolean } {
  if (response.ok || response.status === 200) {
    let message = "formattedId::NPT_SERVER_MESSAGE_SUCCESS";
    if (options?.successMessage) {
      message =
        typeof options.successMessage === "function"
          ? options.successMessage(response.status, response.statusText)
          : options.successMessage;
    }
    dispatchSuccessV2(message, dispatch);
    return { valid: true };
  }
  let message = "formattedId::NPT_SERVER_MESSAGE_EMPTY_ERROR";
  if (options?.failMessage) {
    message =
      typeof options.failMessage === "function"
        ? options.failMessage(response.status, response.statusText)
        : options.failMessage;
  }
  dispatchErrorV2(
    message,
    new ServerError(response.status, response.statusText),
    dispatch
  );
  return { valid: false };
}
